/**
 * iOS SDK config — dash data-layer client (feature #183).
 *
 * Thin typed wrapper around `apiClient` for the `/api/v1/ios-sdk` surface.
 * Drives the operator workflow:
 *
 *   1. CRUD on iOS apps — register bundle_id / app_name / team_id /
 *      min_sdk_version, list them, edit per-app config (enabled/disabled
 *      events, rate_limit_per_min, feature_flags, min_sdk_version), and
 *      soft-delete when an app is retired.
 *   2. View recent installations pulled from the telemetry endpoint,
 *      optionally filtered by app.
 *
 * API contract (mirrored from `apps/api/src/routes/ios-sdk.ts`):
 *
 *   GET    /apps                                → { apps: IosApp[] }
 *   POST   /apps { bundle_id, app_name, team_id?, min_sdk_version? }
 *                                               → 201 { app: IosApp }
 *                                               → 409 { error: 'bundle_id_taken' }
 *   PATCH  /apps/:id { app_name?, team_id?, min_sdk_version?, active?, config? }
 *                                               → 200 { app: IosApp }
 *   DELETE /apps/:id                            → { id, deleted, deleted_at }
 *   GET    /config?app_id=...&sdk_version=...   → { ...ConfigResponse }
 *   POST   /installations                       → { installation, deduped }
 *   GET    /installations?app_id=&limit=        → { installations: Installation[] }
 *
 * Pure helpers (validators + diff) are exported so the page tests stay
 * hermetic — no fetch, no router context needed.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Server-shape types
// ---------------------------------------------------------------------------

export interface AppConfig {
	enabled_events?: string[];
	disabled_events?: string[];
	rate_limit_per_min?: number;
	feature_flags?: Record<string, boolean>;
	min_sdk_version?: string;
}

export interface IosApp {
	id: string;
	tenant_id: string;
	bundle_id: string;
	app_name: string;
	team_id: string | null;
	min_sdk_version: string;
	config: AppConfig;
	active: boolean;
	created_at: number;
	updated_at: number;
	deleted_at: number | null;
}

export interface ConfigResponse {
	app_id: string;
	bundle_id: string;
	enabled_events: string[];
	disabled_events: string[];
	rate_limit_per_min: number;
	feature_flags: Record<string, boolean>;
	min_sdk_version: string;
	upgrade_recommended: boolean;
}

export interface Installation {
	id: string;
	tenant_id: string;
	app_id: string;
	install_id: string;
	sdk_version: string;
	device_model: string | null;
	os_version: string | null;
	ua: string | null;
	created_at: number;
}

// ---------------------------------------------------------------------------
// Draft + schema — register app form
// ---------------------------------------------------------------------------

export interface RegisterDraft {
	bundleId: string;
	appName: string;
	teamId: string;
	minSdkVersion: string;
}

export const EMPTY_REGISTER_DRAFT: RegisterDraft = {
	bundleId: "",
	appName: "",
	teamId: "",
	minSdkVersion: "",
};

export interface ValidatedRegisterDraft {
	bundle_id: string;
	app_name: string;
	team_id?: string;
	min_sdk_version?: string;
}

// ---------------------------------------------------------------------------
// Draft + schema — config editor
// ---------------------------------------------------------------------------

export interface ConfigDraft {
	enabledEvents: string[];
	disabledEvents: string[];
	rateLimitPerMin: string; // operator types a number; we coerce.
	featureFlags: Record<string, boolean>;
	minSdkVersion: string;
}

export const EMPTY_CONFIG_DRAFT: ConfigDraft = {
	enabledEvents: [],
	disabledEvents: [],
	rateLimitPerMin: "",
	featureFlags: {},
	minSdkVersion: "",
};

export interface ValidatedConfigDraft {
	enabled_events?: string[];
	disabled_events?: string[];
	rate_limit_per_min?: number;
	feature_flags?: Record<string, boolean>;
	min_sdk_version?: string;
}

// ---------------------------------------------------------------------------
// Parse result
// ---------------------------------------------------------------------------

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

// ---------------------------------------------------------------------------
// Limits — kept in sync with the API route.
// ---------------------------------------------------------------------------

export const MAX_APP_NAME = 120;
export const MAX_BUNDLE_ID = 155;
export const MAX_RATE_LIMIT_PER_MIN = 60_000;
export const MIN_RATE_LIMIT_PER_MIN = 1;

export const KNOWN_EVENTS: readonly string[] = [
	"app_open",
	"app_close",
	"screen_view",
	"tap",
	"purchase",
	"signup",
	"login",
	"custom",
];

const BUNDLE_ID_RE = /^[a-zA-Z][a-zA-Z0-9-]*(\.[a-zA-Z0-9][a-zA-Z0-9-]*)+$/;
const TEAM_ID_RE = /^[A-Z0-9]{10}$/;
const SEMVER_RE = /^\d+\.\d+\.\d+$/;

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export function trimLower(s: string): string {
	return (s ?? "").trim().toLowerCase();
}

export function trimUpper(s: string): string {
	return (s ?? "").trim().toUpperCase();
}

export function isBundleId(s: unknown): s is string {
	if (typeof s !== "string") return false;
	const t = s.trim();
	if (!t || t.length > MAX_BUNDLE_ID) return false;
	return BUNDLE_ID_RE.test(t);
}

export function isTeamId(s: unknown): s is string {
	return typeof s === "string" && TEAM_ID_RE.test(s.trim());
}

export function isSemver(s: unknown): s is string {
	return typeof s === "string" && SEMVER_RE.test(s.trim());
}

export const registerDraftSchema = {
	safeParse(input: RegisterDraft): ParseResult<ValidatedRegisterDraft> {
		const errors: Record<string, string> = {};

		const bundle = trimLower(input.bundleId);
		if (!bundle) {
			errors.bundleId = "Bundle ID is required.";
		} else if (bundle.length > MAX_BUNDLE_ID) {
			errors.bundleId = `Bundle ID must be ${MAX_BUNDLE_ID} characters or fewer.`;
		} else if (!BUNDLE_ID_RE.test(bundle)) {
			errors.bundleId = "Must be reverse-DNS (e.g. com.acme.app).";
		}

		const name = (input.appName ?? "").trim();
		if (!name) {
			errors.appName = "App name is required.";
		} else if (name.length > MAX_APP_NAME) {
			errors.appName = `App name must be ${MAX_APP_NAME} characters or fewer.`;
		}

		let team: string | undefined;
		const teamRaw = (input.teamId ?? "").trim();
		if (teamRaw) {
			const upper = trimUpper(teamRaw);
			if (!TEAM_ID_RE.test(upper)) {
				errors.teamId = "Apple Team ID is 10 uppercase alphanumerics.";
			} else {
				team = upper;
			}
		}

		let ver: string | undefined;
		const verRaw = (input.minSdkVersion ?? "").trim();
		if (verRaw) {
			if (!SEMVER_RE.test(verRaw)) {
				errors.minSdkVersion = "Use strict semver (e.g. 1.0.0).";
			} else {
				ver = verRaw;
			}
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}

		const out: ValidatedRegisterDraft = {
			bundle_id: bundle,
			app_name: name,
		};
		if (team) out.team_id = team;
		if (ver) out.min_sdk_version = ver;
		return { success: true, data: out };
	},
};

export const configDraftSchema = {
	safeParse(input: ConfigDraft): ParseResult<ValidatedConfigDraft> {
		const errors: Record<string, string> = {};
		const out: ValidatedConfigDraft = {};

		const enabled = dedupStrings(input.enabledEvents ?? []);
		const disabled = dedupStrings(input.disabledEvents ?? []);
		if (enabled.length > 0) out.enabled_events = enabled;
		if (disabled.length > 0) out.disabled_events = disabled;

		// Overlap hint — but not an error; the server will treat disabled as
		// winning, so flag it and let the operator decide.
		const overlap = enabled.filter((e) => disabled.includes(e));
		if (overlap.length > 0) {
			errors.disabledEvents = `Both enabled and disabled: ${overlap.join(", ")}.`;
		}

		const rateRaw = (input.rateLimitPerMin ?? "").trim();
		if (rateRaw) {
			const rate = Number(rateRaw);
			if (!Number.isFinite(rate) || !Number.isInteger(rate)) {
				errors.rateLimitPerMin = "Rate limit must be a whole number.";
			} else if (rate < MIN_RATE_LIMIT_PER_MIN || rate > MAX_RATE_LIMIT_PER_MIN) {
				errors.rateLimitPerMin =
					`Pick a value between ${MIN_RATE_LIMIT_PER_MIN} and ${MAX_RATE_LIMIT_PER_MIN}.`;
			} else {
				out.rate_limit_per_min = rate;
			}
		}

		const flags = sanitizeFlags(input.featureFlags ?? {});
		if (Object.keys(flags).length > 0) out.feature_flags = flags;

		const verRaw = (input.minSdkVersion ?? "").trim();
		if (verRaw) {
			if (!SEMVER_RE.test(verRaw)) {
				errors.minSdkVersion = "Use strict semver (e.g. 1.0.0).";
			} else {
				out.min_sdk_version = verRaw;
			}
		}

		if (Object.keys(errors).length > 0) {
			return { success: false, errors };
		}
		return { success: true, data: out };
	},
};

function dedupStrings(list: string[]): string[] {
	const seen = new Set<string>();
	const out: string[] = [];
	for (const raw of list) {
		if (typeof raw !== "string") continue;
		const t = raw.trim();
		if (!t || seen.has(t)) continue;
		seen.add(t);
		out.push(t);
	}
	return out;
}

function sanitizeFlags(raw: Record<string, boolean>): Record<string, boolean> {
	const out: Record<string, boolean> = {};
	for (const [k, v] of Object.entries(raw ?? {})) {
		if (!k.trim()) continue;
		if (typeof v !== "boolean") continue;
		out[k.trim()] = v;
	}
	return out;
}

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function formatTs(seconds: number | null | undefined): string {
	if (seconds === null || seconds === undefined || Number.isNaN(seconds)) {
		return "—";
	}
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "—";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

export function describeStatus(active: boolean): {
	label: string;
	tone: "ok" | "warn";
} {
	return active
		? { label: "Active", tone: "ok" }
		: { label: "Disabled", tone: "warn" };
}

export function toConfigDraft(config: AppConfig | null | undefined): ConfigDraft {
	const c = config ?? {};
	return {
		enabledEvents: [...(c.enabled_events ?? [])],
		disabledEvents: [...(c.disabled_events ?? [])],
		rateLimitPerMin:
			typeof c.rate_limit_per_min === "number" ? String(c.rate_limit_per_min) : "",
		featureFlags: { ...(c.feature_flags ?? {}) },
		minSdkVersion: typeof c.min_sdk_version === "string" ? c.min_sdk_version : "",
	};
}

export interface ConfigDiffSummary {
	enabled_added: string[];
	enabled_removed: string[];
	disabled_added: string[];
	disabled_removed: string[];
	rate_changed: boolean;
	flags_changed: string[];
	min_sdk_changed: boolean;
	any: boolean;
}

/** Human-friendly diff for the dash save-preview — mirrors the API helper. */
export function summariseConfigDiff(
	before: AppConfig | null | undefined,
	after: ValidatedConfigDraft,
): ConfigDiffSummary {
	const bEnabled = new Set(before?.enabled_events ?? []);
	const aEnabled = new Set(after.enabled_events ?? []);
	const bDisabled = new Set(before?.disabled_events ?? []);
	const aDisabled = new Set(after.disabled_events ?? []);

	const enabled_added = [...aEnabled].filter((x) => !bEnabled.has(x));
	const enabled_removed = [...bEnabled].filter((x) => !aEnabled.has(x));
	const disabled_added = [...aDisabled].filter((x) => !bDisabled.has(x));
	const disabled_removed = [...bDisabled].filter((x) => !aDisabled.has(x));

	const rate_changed = (before?.rate_limit_per_min ?? null) !== (after.rate_limit_per_min ?? null);

	const bFlags = before?.feature_flags ?? {};
	const aFlags = after.feature_flags ?? {};
	const flags_changed: string[] = [];
	const flagKeys = new Set([...Object.keys(bFlags), ...Object.keys(aFlags)]);
	for (const k of flagKeys) {
		if ((bFlags[k] ?? null) !== (aFlags[k] ?? null)) flags_changed.push(k);
	}

	const min_sdk_changed =
		(before?.min_sdk_version ?? null) !== (after.min_sdk_version ?? null);

	const any =
		enabled_added.length > 0 ||
		enabled_removed.length > 0 ||
		disabled_added.length > 0 ||
		disabled_removed.length > 0 ||
		rate_changed ||
		flags_changed.length > 0 ||
		min_sdk_changed;

	return {
		enabled_added,
		enabled_removed,
		disabled_added,
		disabled_removed,
		rate_changed,
		flags_changed,
		min_sdk_changed,
		any,
	};
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/ios-sdk";

export interface RegisterOutcome {
	kind: "created" | "bundle_taken";
	app: IosApp | null;
	message: string | null;
}

export async function registerApp(
	draft: ValidatedRegisterDraft,
): Promise<RegisterOutcome> {
	try {
		const res = await apiClient.post<{ app: IosApp }>(`${BASE}/apps`, draft);
		return { kind: "created", app: res.app, message: null };
	} catch (err) {
		if (err instanceof ApiError && err.status === 409) {
			return {
				kind: "bundle_taken",
				app: null,
				message: "This bundle ID is already registered in this workspace.",
			};
		}
		throw err;
	}
}

export async function listApps(): Promise<IosApp[]> {
	const res = await apiClient.get<{ apps: IosApp[] }>(`${BASE}/apps`);
	return res.apps;
}

export async function patchApp(
	appId: string,
	patch: {
		app_name?: string;
		team_id?: string | null;
		min_sdk_version?: string;
		active?: boolean;
		config?: ValidatedConfigDraft;
	},
): Promise<IosApp> {
	const res = await apiClient.patch<{ app: IosApp }>(
		`${BASE}/apps/${encodeURIComponent(appId)}`,
		patch,
	);
	return res.app;
}

export async function deleteApp(appId: string): Promise<void> {
	await apiClient.delete(`${BASE}/apps/${encodeURIComponent(appId)}`);
}

export async function fetchConfig(
	appId: string,
	sdkVersion?: string,
): Promise<ConfigResponse> {
	return apiClient.get<ConfigResponse>(`${BASE}/config`, {
		query: {
			app_id: appId,
			...(sdkVersion ? { sdk_version: sdkVersion } : {}),
		},
	});
}

export async function listInstallations(opts?: {
	appId?: string;
	limit?: number;
}): Promise<Installation[]> {
	const query: Record<string, string | number> = {};
	if (opts?.appId) query.app_id = opts.appId;
	if (opts?.limit !== undefined) query.limit = opts.limit;
	const res = await apiClient.get<{ installations: Installation[] }>(
		`${BASE}/installations`,
		{ query },
	);
	return res.installations;
}

// ---------------------------------------------------------------------------
// TanStack Query keys
// ---------------------------------------------------------------------------

export const iosSdkQueryKeys = {
	all: ["ios-sdk"] as const,
	apps: () => [...iosSdkQueryKeys.all, "apps"] as const,
	config: (appId: string, sdk?: string) =>
		[...iosSdkQueryKeys.all, "config", appId, sdk ?? null] as const,
	installations: (appId?: string) =>
		[...iosSdkQueryKeys.all, "installations", appId ?? null] as const,
};
